import { NgClass, SlicePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, TrackByFunction, inject } from '@angular/core';

import {
    CrmPromoIdsMap,
    EventModel,
    EventOption,
    EventOptionGroup,
    EventParticipant,
    OptionGroup2,
} from '@frontend/sports/betting-offer/feature/model';
import { ShortNameModule, ShortTeamNamesService } from '@frontend/sports/betting-offer/feature/short-team-names';
import { MarketGroupingConfig, Sitecore } from '@frontend/sports/common/client-config-data-access';
import { SourceOfPick } from '@frontend/sports/option/pick/feature/tracking';
import { RxFor } from '@rx-angular/template/for';
import { orderBy } from 'lodash-es';

import { SHEET_VIEW_OPEN } from '../sheet-view-core/sheet-view.token';
import { MarketHelpComponent } from './market-help/market-help.component';
import { OptionAttributeNameStrategy } from './option-attribute-name.strategy';
import { OptionGroup, OptionGroupService } from './option-group.service';
import { OptionNameStrategy } from './option-name.strategy';
import { OptionPanelBottomActionComponent, OptionPanelBottomActionState, ShowMoreData } from './option-panel-bottom-action.component';
import { OptionComponent } from './option.component';

@Component({
    selector: 'ms-spread-option-group',
    templateUrl: './spread-option-group.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [{ provide: OptionNameStrategy, useClass: OptionAttributeNameStrategy }],
    imports: [RxFor, NgClass, SlicePipe, OptionComponent, MarketHelpComponent, OptionPanelBottomActionComponent],
})
export class SpreadOptionGroupComponent implements OnChanges {
    @Input() event: EventModel;
    @Input() tracking: string;
    @Input() trackingModuleType = '';
    @Input() optionGroup: EventOptionGroup;
    @Input() showMarketHelp: boolean;
    @Input() crmPromoIdsMap: CrmPromoIdsMap;
    @Input() eventHelpText?: string;
    @Input() sourceOfPick = SourceOfPick.Other;
    @Input() betDetails = '';
    @Input() optionsLimitOverride?: number;

    @Output() showAllRequested = new EventEmitter<void>();

    readonly isInSheetView = Boolean(inject(SHEET_VIEW_OPEN, { optional: true }));
    options: EventOption[] = [];
    optionGroups: OptionGroup2[];
    firstParticipant: string;
    secondParticipant: string;
    expanded = false;
    showMoreLessButtonVisible = false;
    tupleClass: string;
    numberOfOptions = 0;
    rows = 0;

    private maxNumberOfVisibleOptions: number;
    visibleOptionsLimit: number;

    showMoreData: ShowMoreData;

    constructor(
        public sitecore: Sitecore,
        marketGroupingConfig: MarketGroupingConfig,
        private optionGroupService: OptionGroupService,
        private shortTeamNamesService: ShortTeamNamesService,
    ) {
        this.maxNumberOfVisibleOptions = marketGroupingConfig.spreadGroupMaxNumberOfLines;
    }

    gridClasses = new Map<number, string>([
        [2, 'double'],
        [3, 'triple'],
        [4, 'quadruple'],
        [5, 'quintuple'],
        [6, 'sextuple'],
    ]);

    ngOnChanges(changes: SimpleChanges): void {
        if (this.optionsLimitOverride) {
            this.maxNumberOfVisibleOptions = this.optionsLimitOverride;
        }

        if (changes.optionGroup?.currentValue) {
            this.setOptionGroups();
        }

        this.setParticipants();
        this.numberOfOptions = this.optionGroups[0]?.options.length;
        this.updateOptionAttr();

        this.showMoreLessButtonVisible = this.optionGroups.length > this.maxNumberOfVisibleOptions;
        this.tupleClass = this.getTupleClass();

        if (changes.event || changes.optionGroup || changes.optionsLimitOverride) {
            const totalOptions = this.optionGroups.length;
            this.showMoreData = {
                totalCount: totalOptions,
                initialVisibleCount: this.maxNumberOfVisibleOptions,
                pageSize: totalOptions,
            };
        }
        this.calculateMaxNumberOfVisibleOptions();
    }

    setOptionGroups(): void {
        this.optionGroups = orderBy(
            this.optionGroupService.getOptionGroups(this.optionGroup, (option) => option.optionGroupId),
            ['optionAttribute'],
        );

        this.optionGroups = this.optionGroups.map((og) =>
            this.optionGroupService.orderBasedOnParticipants(og, this.event.participants, this.event.viewType),
        );
    }

    trackGroup: TrackByFunction<OptionGroup | OptionGroup2> = (index, group) => {
        return group.attribute;
    };

    trackOption: TrackByFunction<EventOption | undefined> = (index, option) => {
        return option ? option.id : `empty-${index}`;
    };

    onShowMoreLessButtonToggle(): void {
        this.expanded = !this.expanded;
        this.visibleOptionsLimit = this.expanded ? this.optionGroups.length : this.maxNumberOfVisibleOptions;
        this.rows = Math.min(this.optionGroups.length, this.visibleOptionsLimit);
    }

    onShowMore({ state }: { state: OptionPanelBottomActionState; visibleCount: number }): void {
        this.expanded = state !== 'collapsed';
        this.calculateMaxNumberOfVisibleOptions();
    }

    protected setParticipants(): void {
        this.firstParticipant = this.getParticipantName(this.event?.participants[0]);
        this.secondParticipant = this.getParticipantName(this.event?.participants[1]);
    }

    private getParticipantName(participant: EventParticipant): string {
        if (!participant) {
            return '';
        }

        if (this.shortTeamNamesService.shouldUseShortName(participant, this.event.sport.id, this.event.league.id, ShortNameModule.Spread)) {
            return participant.shortName!;
        }

        return participant.name;
    }

    protected updateOptionAttr(): void {
        if (!this.numberOfOptions || this.numberOfOptions <= 2) {
            return;
        }
        this.optionGroups.forEach((group: OptionGroup2) => {
            if (group.options[0]?.optionAttribute && group.options[2]?.optionAttribute) {
                group.options[1] && (group.options[1].optionAttribute = group.options[0].optionAttribute + '/' + group.options[2].optionAttribute);
            }
        });
    }

    private calculateMaxNumberOfVisibleOptions(): void {
        this.visibleOptionsLimit = this.isInSheetView || this.expanded ? this.optionGroups.length : this.maxNumberOfVisibleOptions;
        this.rows = Math.min(this.optionGroups.length, this.visibleOptionsLimit);
    }

    private getTupleClass(): string {
        return this.gridClasses.get(this.numberOfOptions) ?? '';
    }
}
